import { ReactElement, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Heading, Paragraph, Skeleton, Tag } from '@digdir/designsystemet-react';
import { OrganizationLayout } from '../../../../components/OrganizationLayout';
import { RolesUtils } from '../../../../utils/RolesUtils';
import { useGetOrganizationUsersQuery } from '../../../../hooks';
import { Routes } from '../../../../enums/Routes';
import { buildRoute } from '../../../../utils/routeBuilder';
import { UserSummary } from '../components/UserSummary';
import classes from '../Users.module.css';

const VISIBLE_TEAMS = 12;

export const ViewUserPage = (): ReactElement => {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const { data: organizationUsers, isLoading, isError } = useGetOrganizationUsersQuery();
  const [showAllTeams, setShowAllTeams] = useState(false);

  const user = organizationUsers?.find((u) => u.id === Number(userId));
  const roleInfo = RolesUtils.getAdministerableRoles();
  const roles = user
    ? RolesUtils.filterAdministerableRoles(user.roles).map(
        (role) => roleInfo.find((info) => info.key === role) ?? { key: role, label: role, description: '' },
      )
    : [];
  const teams = user && !showAllTeams ? user.teamNames.slice(0, VISIBLE_TEAMS) : (user?.teamNames ?? []);

  const content = () => {
    if (isLoading) {
      return (
        <div aria-busy="true">
          <Skeleton variant="rectangle" width="100%" height={320} />
        </div>
      );
    }
    if (isError || !user) {
      return (
        <Alert data-color="danger">
          <Paragraph>{t('users.view.errorMessage')}</Paragraph>
        </Alert>
      );
    }

    return (
      <>
        <section aria-label={t('organizationPage.users.details')} className={classes.card}>
          <UserSummary user={user} />

          <div className={classes.block}>
            <div className={classes.blockHeader}>
              <Heading level={3} data-size="xs">
                {t('users.view.sections.roles')}
              </Heading>
              <Button asChild variant="tertiary" data-size="sm">
                <Link to={buildRoute(Routes.OrganizationEditUser, { userId: user.id })}>
                  {t('users.view.buttons.editRoles')}
                </Link>
              </Button>
            </div>
            {roles.length === 0 ? (
              <Paragraph data-size="sm" className={classes.subtle}>
                {t('users.view.empty.roles')}
              </Paragraph>
            ) : (
              <ul className={classes.roleList}>
                {roles.map((role) => (
                  <li key={role.key}>
                    <span className={classes.roleName}>{role.label}</span>
                    {role.description && <span className={classes.subtle}>{role.description}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {user.safetyRep && (
            <div className={classes.block}>
              <Heading level={3} data-size="xs">
                {user.safetyRep.isMain
                  ? t('organizationPage.users.safetyRep.mainTitle')
                  : t('organizationPage.users.safetyRep.title')}
              </Heading>
              {user.safetyRep.teams.length === 0 ? (
                <Paragraph data-size="sm" className={classes.subtle}>
                  {t('organizationPage.users.safetyRep.wholeOrganization')}
                </Paragraph>
              ) : (
                <ul className={classes.teamTags} aria-label={t('organizationPage.users.safetyRep.teamsFor')}>
                  {user.safetyRep.teams.map((team) => (
                    <li key={team.id}>
                      <Tag data-size="sm" data-color="accent">
                        {team.name}
                      </Tag>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className={classes.block}>
            <Heading level={3} data-size="xs">
              {t('users.view.sections.team')}{' '}
              <span className={classes.count}>({user.teamNames.length})</span>
            </Heading>
            {user.teamNames.length === 0 ? (
              <Paragraph data-size="sm" className={classes.subtle}>
                {t('users.view.empty.teams')}
              </Paragraph>
            ) : (
              <ul className={classes.teamTags}>
                {teams.map((teamName) => (
                  <li key={teamName}>
                    <Tag data-size="sm" data-color="accent">
                      {teamName}
                    </Tag>
                  </li>
                ))}
              </ul>
            )}
            {user.teamNames.length > VISIBLE_TEAMS && (
              <div>
                <Button
                  variant="tertiary"
                  data-size="sm"
                  aria-expanded={showAllTeams}
                  onClick={() => setShowAllTeams((show) => !show)}
                >
                  {showAllTeams
                    ? t('organizationPage.users.showFewerTeams')
                    : t('organizationPage.users.showAllTeams', { count: user.teamNames.length })}
                </Button>
              </div>
            )}
          </div>
        </section>
        <div>
          <Button asChild variant="secondary">
            <Link to={Routes.OrganizationUsersOverviewPage}>{t('organizationPage.users.backToUsers')}</Link>
          </Button>
        </div>
      </>
    );
  };

  return (
    <OrganizationLayout
      breadcrumbs={[
        { label: t('organizationPage.tabs.users'), to: Routes.OrganizationUsersOverviewPage },
        { label: user?.email ?? t('breadcrumbs.viewUser'), to: '' },
      ]}
    >
      {content()}
    </OrganizationLayout>
  );
};
